/**
 * Universal Output Framework (UOF) - React Components
 * 
 * A standardized component library for rendering agent outputs
 * consistently across the platform.
 * 
 * @version 2.0
 */

import React, { useState, useMemo } from 'react';
import {
  ArrowSquareOut,
  CaretDown,
  CaretUp,
  ChartBar,
  ChatText,
  Check,
  CheckCircle,
  Clock,
  Copy,
  DownloadSimple,
  FileText,
  Info,
  Lightning,
  Minus,
  Sparkle,
  Target,
  TrendDown,
  TrendUp,
  Trophy,
  User,
  Warning,
  WarningCircle,
  XCircle
} from '@phosphor-icons/react';

// ════════════════════════════════════════════════════════════════════════════
// STATUS & SCORE UTILITIES
// ════════════════════════════════════════════════════════════════════════════

const STATUS_CONFIG = {
  excellent: { color: 'emerald', icon: CheckCircle, label: 'Excellent' },
  good: { color: 'green', icon: CheckCircle, label: 'Good' },
  fair: { color: 'amber', icon: WarningCircle, label: 'Fair' },
  poor: { color: 'orange', icon: Warning, label: 'Poor' },
  critical: { color: 'red', icon: XCircle, label: 'Critical' },
  success: { color: 'emerald', icon: CheckCircle, label: 'Success' },
  warning: { color: 'amber', icon: WarningCircle, label: 'Warning' },
  error: { color: 'red', icon: XCircle, label: 'Error' },
  info: { color: 'blue', icon: Info, label: 'Info' },
  pending: { color: 'gray', icon: Clock, label: 'Pending' },
};

const PRIORITY_CONFIG = {
  critical: { color: 'red', bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400' },
  high: { color: 'orange', bg: 'bg-orange-500/10', border: 'border-orange-500/30', text: 'text-orange-400' },
  medium: { color: 'amber', bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400' },
  low: { color: 'blue', bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400' },
  info: { color: 'gray', bg: 'bg-gray-500/10', border: 'border-gray-500/30', text: 'text-gray-400' },
};

const getStatusConfig = (status) => STATUS_CONFIG[status] || STATUS_CONFIG.info;
const getPriorityConfig = (priority) => PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.medium;

// ════════════════════════════════════════════════════════════════════════════
// SCORE COMPONENTS
// ════════════════════════════════════════════════════════════════════════════

/**
 * Circular score indicator
 */
export const ScoreCircle = ({ value, max = 100, size = 'md', showLabel = true, label }) => {
  const percentage = max > 0 ? (value / max) * 100 : 0;
  const circumference = 2 * Math.PI * 40;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  
  const sizeClasses = {
    sm: 'w-16 h-16',
    md: 'w-24 h-24',
    lg: 'w-32 h-32',
    xl: 'w-40 h-40',
  };
  
  const getColor = () => {
    if (percentage >= 90) return 'text-emerald-500';
    if (percentage >= 70) return 'text-green-500';
    if (percentage >= 50) return 'text-amber-500';
    if (percentage >= 30) return 'text-orange-500';
    return 'text-red-500';
  };
  
  return (
    <div className="flex flex-col items-center gap-2">
      <div className={`relative ${sizeClasses[size]}`}>
        <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
          {/* Background circle */}
          <circle
            cx="50"
            cy="50"
            r="40"
            fill="none"
            stroke="currentColor"
            strokeWidth="8"
            className="text-white/5"
          />
          {/* Progress circle */}
          <circle
            cx="50"
            cy="50"
            r="40"
            fill="none"
            stroke="currentColor"
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            className={`${getColor()} transition-all duration-1000 ease-out`}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`font-bold ${size === 'sm' ? 'text-lg' : size === 'lg' ? 'text-3xl' : 'text-2xl'} text-white`}>
            {Math.round(percentage)}
          </span>
          <span className="text-xs text-gray-400">/ {max}</span>
        </div>
      </div>
      {showLabel && label && (
        <span className="text-sm text-gray-400 font-medium">{label}</span>
      )}
    </div>
  );
};

/**
 * Linear score bar
 */
export const ScoreBar = ({ value, max = 100, label, status, showValue = true }) => {
  const percentage = max > 0 ? (value / max) * 100 : 0;
  const statusConfig = getStatusConfig(status || (percentage >= 70 ? 'good' : percentage >= 50 ? 'fair' : 'poor'));
  
  const getGradient = () => {
    if (percentage >= 90) return 'from-emerald-500 to-emerald-400';
    if (percentage >= 70) return 'from-green-500 to-green-400';
    if (percentage >= 50) return 'from-amber-500 to-amber-400';
    if (percentage >= 30) return 'from-orange-500 to-orange-400';
    return 'from-red-500 to-red-400';
  };
  
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm text-white font-medium">{label}</span>
        {showValue && (
          <span className="text-sm text-gray-400">
            {value}/{max}
          </span>
        )}
      </div>
      <div className="h-2 bg-white/5 rounded-full overflow-hidden">
        <div
          className={`h-full bg-gradient-to-r ${getGradient()} rounded-full transition-all duration-700 ease-out`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

/**
 * Score badge/pill
 */
export const ScoreBadge = ({ value, max = 100, label, size = 'md' }) => {
  const percentage = max > 0 ? (value / max) * 100 : 0;
  
  const getColors = () => {
    if (percentage >= 90) return 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400';
    if (percentage >= 70) return 'bg-green-500/10 border-green-500/30 text-green-400';
    if (percentage >= 50) return 'bg-amber-500/10 border-amber-500/30 text-amber-400';
    if (percentage >= 30) return 'bg-orange-500/10 border-orange-500/30 text-orange-400';
    return 'bg-red-500/10 border-red-500/30 text-red-400';
  };
  
  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-1.5 text-base',
  };
  
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border font-medium ${getColors()} ${sizeClasses[size]}`}>
      {label && <span className="text-white/70">{label}:</span>}
      <span>{value}/{max}</span>
    </span>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// SECTION COMPONENTS
// ════════════════════════════════════════════════════════════════════════════

/**
 * Section wrapper with optional collapse
 */
export const Section = ({ 
  title, 
  subtitle,
  icon, 
  children, 
  collapsible = false, 
  defaultCollapsed = false,
  variant = 'default',
  className = ''
}) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  
  const variantClasses = {
    default: 'bg-white/[0.02] border-white/5',
    highlight: 'bg-gradient-to-r from-indigo-500/5 to-purple-500/5 border-indigo-500/10',
    success: 'bg-emerald-500/5 border-emerald-500/10',
    warning: 'bg-amber-500/5 border-amber-500/10',
    error: 'bg-red-500/5 border-red-500/10',
  };
  
  return (
    <div className={`rounded-2xl border ${variantClasses[variant]} overflow-hidden ${className}`}>
      {title && (
        <div 
          className={`flex items-center justify-between px-5 py-4 border-b border-white/5 ${collapsible ? 'cursor-pointer hover:bg-white/[0.02]' : ''}`}
          onClick={collapsible ? () => setIsCollapsed(!isCollapsed) : undefined}
        >
          <div className="flex items-center gap-3">
            {icon && <span className="text-xl">{icon}</span>}
            <div>
              <h3 className="text-sm font-semibold text-white uppercase tracking-wide">{title}</h3>
              {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
            </div>
          </div>
          {collapsible && (
            <button className="p-1 text-gray-400 hover:text-white transition-colors">
              {isCollapsed ? <CaretDown size={18} /> : <CaretUp size={18} />}
            </button>
          )}
        </div>
      )}
      {!isCollapsed && (
        <div className="p-5">
          {children}
        </div>
      )}
    </div>
  );
};

/**
 * Hero section for key findings
 */
export const HeroSection = ({ title, content, icon = '✨', score }) => {
  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-500/10 via-purple-500/5 to-pink-500/10 border border-white/10 p-6">
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-indigo-500/20 to-transparent rounded-full blur-2xl -translate-y-1/2 translate-x-1/2" />
      
      <div className="relative flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-xl shadow-lg flex-shrink-0">
          {icon}
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-bold text-white mb-2">{title}</h3>
          <p className="text-gray-300 text-sm leading-relaxed">{content}</p>
        </div>
        {score && (
          <ScoreCircle value={score.value} max={score.max} size="md" label={score.label} />
        )}
      </div>
    </div>
  );
};

/**
 * Scorecard section with multiple metrics
 */
export const ScorecardSection = ({ items = [], title, icon }) => {
  return (
    <Section title={title} icon={icon}>
      <div className="space-y-4">
        {items.map((item, idx) => (
          <ScoreBar
            key={idx}
            value={item.score}
            max={item.max_score || item.max || 100}
            label={item.label}
            status={item.status}
          />
        ))}
      </div>
    </Section>
  );
};

/**
 * Prose/markdown section
 */
export const ProseSection = ({ content, title, icon, collapsible }) => {
  // Simple markdown-like rendering
  const renderContent = (text) => {
    if (!text) return null;
    
    return text.split('\n').map((line, idx) => {
      // Bold text
      line = line.replace(/\*\*(.+?)\*\*/g, '<strong class="text-white font-semibold">$1</strong>');
      // Italic
      line = line.replace(/\*(.+?)\*/g, '<em>$1</em>');
      // Bullet points
      if (line.match(/^[-•*]\s+/)) {
        const text = line.replace(/^[-•*]\s+/, '');
        return (
          <div key={idx} className="flex items-start gap-2 py-1">
            <span className="text-indigo-400 mt-1">•</span>
            <span dangerouslySetInnerHTML={{ __html: text }} />
          </div>
        );
      }
      // Numbered lists
      const numMatch = line.match(/^(\d+)[.)]\s+(.+)/);
      if (numMatch) {
        return (
          <div key={idx} className="flex items-start gap-2 py-1">
            <span className="text-indigo-400 font-medium min-w-[1.5rem]">{numMatch[1]}.</span>
            <span dangerouslySetInnerHTML={{ __html: numMatch[2] }} />
          </div>
        );
      }
      // Empty line = paragraph break
      if (!line.trim()) {
        return <div key={idx} className="h-3" />;
      }
      // Regular text
      return (
        <p key={idx} className="py-0.5" dangerouslySetInnerHTML={{ __html: line }} />
      );
    });
  };
  
  return (
    <Section title={title} icon={icon} collapsible={collapsible}>
      <div className="text-gray-300 text-sm leading-relaxed space-y-1">
        {renderContent(content)}
      </div>
    </Section>
  );
};

/**
 * Key-value details section
 */
export const KeyValueSection = ({ items = [], title, icon }) => {
  return (
    <Section title={title} icon={icon}>
      <div className="space-y-3">
        {items.map((item, idx) => (
          <div key={idx} className="flex items-start justify-between gap-4 py-2 border-b border-white/5 last:border-0">
            <div className="flex items-center gap-2 text-gray-400 text-sm">
              {item.icon && <span>{item.icon}</span>}
              <span>{item.key}</span>
            </div>
            <div className="text-white text-sm font-medium text-right">
              {item.type === 'badge' ? (
                <span className="px-2 py-0.5 bg-indigo-500/10 border border-indigo-500/30 rounded text-indigo-400 text-xs">
                  {item.value}
                </span>
              ) : item.type === 'link' ? (
                <a href={item.value} className="text-indigo-400 hover:text-indigo-300 flex items-center gap-1">
                  {item.value} <ArrowSquareOut size={12} />
                </a>
              ) : (
                item.value
              )}
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
};

/**
 * Checklist/recommendations section
 */
export const ChecklistSection = ({ items = [], title, icon }) => {
  return (
    <Section title={title} icon={icon}>
      <div className="space-y-2">
        {items.map((item, idx) => {
          const config = getPriorityConfig(item.priority);
          return (
            <div
              key={idx}
              className={`flex items-start gap-3 p-3 rounded-xl ${config.bg} border ${config.border}`}
            >
              <div className={`w-5 h-5 rounded-full border-2 ${config.border} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                {item.completed && <Check size={12} className={config.text} />}
              </div>
              <div className="flex-1">
                <p className="text-white text-sm">{item.text}</p>
                {item.category && (
                  <span className="text-xs text-gray-500 mt-1">{item.category}</span>
                )}
              </div>
              <span className={`text-xs px-2 py-0.5 rounded ${config.bg} ${config.text} capitalize`}>
                {item.priority}
              </span>
            </div>
          );
        })}
      </div>
    </Section>
  );
};

/**
 * Data table section
 */
export const TableSection = ({ columns = [], rows = [], title, icon }) => {
  return (
    <Section title={title} icon={icon}>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/10">
              {columns.map((col, idx) => (
                <th
                  key={idx}
                  className={`px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide text-${col.align || 'left'}`}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIdx) => (
              <tr key={rowIdx} className="border-b border-white/5 hover:bg-white/[0.02]">
                {columns.map((col, colIdx) => (
                  <td
                    key={colIdx}
                    className={`px-4 py-3 text-sm text-gray-300 text-${col.align || 'left'}`}
                  >
                    {row[col.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
};

/**
 * Transcript section with speaker labels
 */
export const TranscriptSection = ({ entries = [], title, icon, collapsible = true }) => {
  const getSentimentColor = (sentiment) => {
    switch (sentiment) {
      case 'positive': return 'border-l-emerald-500';
      case 'negative': return 'border-l-red-500';
      default: return 'border-l-gray-500';
    }
  };
  
  return (
    <Section title={title} icon={icon} collapsible={collapsible} defaultCollapsed={true}>
      <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
        {entries.map((entry, idx) => (
          <div
            key={idx}
            className={`pl-4 py-2 border-l-2 ${getSentimentColor(entry.sentiment)} ${entry.highlight ? 'bg-indigo-500/10 rounded-r-lg' : ''}`}
          >
            <div className="flex items-center gap-2 mb-1">
              <User size={14} className="text-gray-500" />
              <span className="text-xs font-semibold text-indigo-400">{entry.speaker}</span>
              {entry.timestamp && (
                <span className="text-xs text-gray-600">{entry.timestamp}</span>
              )}
            </div>
            <p className="text-sm text-gray-300">{entry.text}</p>
          </div>
        ))}
      </div>
    </Section>
  );
};

/**
 * WarningCircle/notice section
 */
export const AlertSection = ({ alert }) => {
  if (!alert) return null;
  
  const config = getStatusConfig(alert.type);
  const Icon = config.icon;
  
  const bgColors = {
    success: 'bg-emerald-500/10 border-emerald-500/30',
    warning: 'bg-amber-500/10 border-amber-500/30',
    error: 'bg-red-500/10 border-red-500/30',
    info: 'bg-blue-500/10 border-blue-500/30',
  };
  
  const textColors = {
    success: 'text-emerald-400',
    warning: 'text-amber-400',
    error: 'text-red-400',
    info: 'text-blue-400',
  };
  
  return (
    <div className={`flex items-start gap-3 p-4 rounded-xl border ${bgColors[alert.type] || bgColors.info}`}>
      <Icon size={20} className={textColors[alert.type] || textColors.info} />
      <div>
        {alert.title && (
          <h4 className={`font-semibold mb-1 ${textColors[alert.type] || textColors.info}`}>
            {alert.title}
          </h4>
        )}
        <p className="text-sm text-gray-300">{alert.message}</p>
      </div>
    </div>
  );
};

/**
 * Metrics grid section
 */
export const MetricsGridSection = ({ items = [], title, icon }) => {
  return (
    <Section title={title} icon={icon}>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {items.map((item, idx) => {
          const percentage = item.max_score > 0 ? (item.score / item.max_score) * 100 : 0;
          const statusConfig = getStatusConfig(item.status);
          
          return (
            <div
              key={idx}
              className="p-4 rounded-xl bg-white/[0.02] border border-white/5 hover:border-white/10 transition-colors"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-500 uppercase tracking-wide">{item.label}</span>
                {item.trend && item.trend !== 'none' && (
                  <span className={item.trend === 'up' ? 'text-emerald-400' : 'text-red-400'}>
                    {item.trend === 'up' ? <TrendUp size={14} /> : <TrendDown size={14} />}
                  </span>
                )}
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-bold text-white">{item.score}</span>
                <span className="text-sm text-gray-500">/{item.max_score || 100}</span>
              </div>
              <div className="mt-2 h-1.5 bg-white/5 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${
                    percentage >= 70 ? 'bg-emerald-500' : percentage >= 50 ? 'bg-amber-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${percentage}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// MAIN UNIVERSAL OUTPUT VIEWER
// ════════════════════════════════════════════════════════════════════════════

/**
 * Universal Output Viewer - Main component
 * Renders any UOS-compliant output with consistent styling
 */
export const UniversalOutputViewer = ({ output, onExport }) => {
  const [copied, setCopied] = useState(false);
  
  if (!output) {
    return (
      <div className="p-8 text-center text-gray-500">
        <FileText size={48} className="mx-auto mb-4 opacity-50" />
        <p>No output data available</p>
      </div>
    );
  }
  
  // Handle legacy format (raw markdown string)
  if (typeof output === 'string') {
    return (
      <div className="space-y-6">
        <ProseSection content={output} title="Output" icon="📄" />
      </div>
    );
  }
  
  const { header, summary, sections = [], export: exportConfig, raw_markdown } = output;
  
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(raw_markdown || JSON.stringify(output, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };
  
  // Render a section based on its type
  const renderSection = (section) => {
    switch (section.type) {
      case 'hero':
        return <HeroSection key={section.id} {...section} />;
      
      case 'scorecard':
      case 'metrics_grid':
        return section.type === 'metrics_grid' 
          ? <MetricsGridSection key={section.id} items={section.items} title={section.title} icon={section.icon} />
          : <ScorecardSection key={section.id} items={section.items} title={section.title} icon={section.icon} />;
      
      case 'prose':
        return <ProseSection key={section.id} content={section.content} title={section.title} icon={section.icon} collapsible={section.collapsible} />;
      
      case 'key_value':
        return <KeyValueSection key={section.id} items={section.items} title={section.title} icon={section.icon} />;
      
      case 'checklist':
        return <ChecklistSection key={section.id} items={section.items} title={section.title} icon={section.icon} />;
      
      case 'table':
        return <TableSection key={section.id} columns={section.columns} rows={section.rows} title={section.title} icon={section.icon} />;
      
      case 'transcript':
        return <TranscriptSection key={section.id} entries={section.entries} title={section.title} icon={section.icon} collapsible={section.collapsible} />;
      
      case 'alert':
        return <AlertSection key={section.id} alert={section.alert} />;
      
      default:
        // Fallback to prose for unknown types
        if (section.content) {
          return <ProseSection key={section.id} content={section.content} title={section.title} icon={section.icon} />;
        }
        return null;
    }
  };
  
  return (
    <div className="space-y-6">
      {/* Header */}
      {header && (
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            {header.icon && (
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-2xl shadow-lg">
                {header.icon}
              </div>
            )}
            <div>
              <h1 className="text-xl font-bold text-white">{header.title}</h1>
              {header.subtitle && (
                <p className="text-sm text-gray-400 mt-1">{header.subtitle}</p>
              )}
              {header.timestamp && (
                <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                  <Clock size={12} />
                  {new Date(header.timestamp).toLocaleString()}
                </p>
              )}
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleCopy}
              className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
              title="Copy to clipboard"
            >
              {copied ? <Check size={18} /> : <Copy size={18} />}
            </button>
            {exportConfig?.formats?.length > 0 && onExport && (
              <button
                onClick={() => onExport(output)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-400 hover:text-indigo-300 transition-colors"
              >
                <DownloadSimple size={16} />
                <span className="text-sm font-medium">Export</span>
              </button>
            )}
          </div>
        </div>
      )}
      
      {/* Summary with overall score */}
      {summary && (summary.score || summary.badges?.length > 0) && (
        <div className="p-6 rounded-2xl bg-gradient-to-r from-indigo-500/5 via-purple-500/5 to-pink-500/5 border border-white/10">
          <div className="flex items-center gap-6">
            {summary.score && (
              <ScoreCircle
                value={summary.score.value}
                max={summary.score.max}
                size="lg"
                label={summary.score.label}
              />
            )}
            <div className="flex-1">
              {summary.one_liner && (
                <p className="text-white font-medium mb-3">{summary.one_liner}</p>
              )}
              {summary.badges?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {summary.badges.map((badge, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-sm text-gray-300"
                    >
                      <Trophy size={14} className="text-amber-400" />
                      {badge}
                    </span>
                  ))}
                </div>
              )}
              {summary.key_findings?.length > 0 && (
                <ul className="mt-3 space-y-1">
                  {summary.key_findings.map((finding, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-400">
                      <Sparkle size={14} className="text-indigo-400 mt-0.5 flex-shrink-0" />
                      {finding}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Sections */}
      <div className="space-y-4">
        {sections.map(renderSection)}
      </div>
    </div>
  );
};

export default UniversalOutputViewer;
